import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'lodash';
import { Button, Modal } from 'reactstrap';

// constant
import appIcons from '../../constants/appIcons';
import LocalStrings from '../../assets/languages';
import {
  INTERVAL_1,
  SCRIPT_URI_ADDON,
  SSO_AUTH_URL,
  TNC_URL
} from '../../constants/constant';
import { validateEmail, validatePhone } from '../Common/base-components';
import { showError } from '../Common/alertmessage';
import {
  getErrorMessageForLoginErrorCode,
  getSameDomainBaseUrl,
  replaceAll
} from '../../util/util-common';
import { MANDATORY_PROFILE_OPEN_TYPES } from '../../constants/section-constants/type-constants';
import { LOGIN_ERROR_EMAIL_NOT_REGISTERED } from '../../constants/section-constants/common-constants';

// actions
import {
  loginModal,
  loginWithEmail,
  loginWithPassCode,
  setProfilePortalToggle
} from '../../actions';
import {
  checkEventSpecificCases,
  EVENT_SPECIFIC_CHECKING_CASES
} from '../../constants/section-constants/event-specific-checks';

// components
import PhoneInput from '../Common/PhoneInput';

const ALLOWED_LAYOUTS = {
  email: 'email',
  pw: 'password'
};
const ALLOWED_REDIRECTS = {
  terms: 'terms',
  support: 'support',
  sso: 'sso'
};
const VALIDATION_INIT = {
  email: '',
  tnc: '',
  passcode: ''
};
const RESEND_OTP_TIME = 30;

function useInterval(callback, delay) {
  useEffect(() => {
    if (delay !== null) {
      const id = setInterval(callback, delay);
      return () => clearInterval(id);
    }
  }, [callback, delay]);
}

const SignIn = props => {
  const {
    session,
    modalStatus,
    eventDetails,
    eventSettings,
    userDetails,
    openEditProfile,
    isEnabledSSO
  } = props;

  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [layout, setLayout] = useState(ALLOWED_LAYOUTS.email); // enum email | password
  const [loginCreds, setLoginCreds] = useState({
    email: '',
    tnc: false
    // passcode: ''
  });
  const [validations, setValidations] = useState(VALIDATION_INIT);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [isDisabled, setIsDisabled] = useState(true);
  const [emailToken, setEmailToken] = useState(null);
  const [showSSODesc, setShowSSODesc] = useState(isEnabledSSO);
  const [usingPhone, setUsingPhone] = useState(false);

  useInterval(() => {
    if (secondsLeft > 0) {
      setSecondsLeft(secondsLeft - 1);
    } else {
      setIsDisabled(false);
    }
  }, INTERVAL_1);

  const allowPhoneLogin = checkEventSpecificCases(
    session?.eventid,
    EVENT_SPECIFIC_CHECKING_CASES.allowLoginWithPhoneNumber
  );

  const closeModal = () => {
    props.loginModal({ showModal: false });
  };

  const handleCredentials = () => {
    setLoading(true);

    const passed = validate();
    if (!passed) {
      setLoading(false);
      return;
    } else {
      setValidations(VALIDATION_INIT);
    }

    if (_.isEmpty(loginCreds?.passcode)) {
      handleResendOTP();
    } else {
      props.loginWithPassCode(
        session,
        {
          passcode: loginCreds?.passcode,
          emailToken
        },
        res => {
          setLoading(false);
          if (res.success) {
            handleSignInComplete();
          } else showError(LocalStrings.dynamic.kInvalidPasscode);
        }
      );
    }

    function validate() {
      const errors = {};
      const email = loginCreds?.email;
      const tnc = loginCreds?.tnc;
      const passcode = loginCreds?.passcode;

      let passed = true;

      if (!email || _.isEmpty(email.trim())) {
        errors.email = LocalStrings.dynamic.kFieldRequired;
        passed = false;
      } else if (email) {
        if (_.includes(email, '@') && !validateEmail(email)) {
          errors.email = LocalStrings.dynamic.kInvalidEmail;
          passed = false;
        } else if (!_.includes(email, '@') && !validatePhone(email)) {
          errors.email = LocalStrings.dynamic.kInvalidPhoneText;
          passed = false;
        } else errors.email = '';
      }

      if (!tnc) {
        errors.tnc = LocalStrings.dynamic.kErrorTerms;
        passed = false;
      } else errors.tnc = '';

      if (_.isEqual(layout, ALLOWED_LAYOUTS.pw) && _.isEmpty(passcode)) {
        errors.passcode = LocalStrings.dynamic.kFieldRequired;
        passed = false;
      } else errors.passcode = '';

      setValidations({ ...validations, ...errors });
      return passed;
    }
  };

  const handleSignInComplete = () => {
    closeModal();

    if (openEditProfile) {
      props.setProfilePortalToggle(userDetails, 'profile');
      history.push({
        pathname: '/myprofile'
      });
    }
  };

  const handleExternalLinkRedirects = type => {
    let url = '';
    switch (type) {
      case ALLOWED_REDIRECTS.support:
        url = `mailto:${eventSettings?.supportemail}`;
        break;

      case ALLOWED_REDIRECTS.terms:
        url = replaceAll(
          TNC_URL,
          ['###BASE_URL###'],
          [getSameDomainBaseUrl(session)]
        );
        break;

      case ALLOWED_REDIRECTS.sso:
        url = `${session.instance_url}${SCRIPT_URI_ADDON}/${replaceAll(
          SSO_AUTH_URL,
          '###EVENTID###',
          `${session?.eventid}`
        )}`;
        break;

      default:
        break;
    }

    if (!_.isEmpty(url)) window.open(url, '_blank');
  };

  const handleBackToEmailLayout = () => {
    let localLoginCreds = _.cloneDeep(loginCreds);
    delete localLoginCreds.passcode;
    setLoginCreds(localLoginCreds);

    setValidations(VALIDATION_INIT);
    setLayout(ALLOWED_LAYOUTS.email);
  };

  const handleResendOTP = () => {
    props.loginWithEmail(session, { email: loginCreds?.email }, res => {
      setLoading(false);

      if (!res.success) {
        if (
          res?.error?.code == 500 &&
          _.isEqual(LOGIN_ERROR_EMAIL_NOT_REGISTERED, res?.error?.message)
        )
          showError(LocalStrings.dynamic.kEmailNotRegistered);
        else showError(LocalStrings.dynamic.kSomethingWentWrongMsg);
      } else if (res?.login) {
        // response comes after validating user for this event check
        handleSignInComplete();
      } else if (res?.data?.token) {
        setEmailToken(res?.data?.token);
        setLayout(ALLOWED_LAYOUTS.pw);
        setSecondsLeft(RESEND_OTP_TIME);
        setIsDisabled(true);
      } else if (res?.data?.errorcode) {
        getErrorMessageForLoginErrorCode(res?.data?.errorcode);
      } else {
        // NOTE: displaying this message to identify that none of the checking passed
        showError(LOGIN_ERROR_EMAIL_NOT_REGISTERED);
      }
    });

    setSecondsLeft(RESEND_OTP_TIME); // Reset the timer
    setIsDisabled(true); // Disable the button again
  };

  return (
    <>
      <Modal
        className="login-modal"
        isOpen={modalStatus && modalStatus.showModal == true ? true : false}
        centered
      >
        <div className="modal-body">
          {/* ----- Modal header section ----- */}
          <header className="d-flex justify-content-between align-items-start mb-3">
            <div className="custom-text5-bold">
              <span className="custom-text-color">//</span>{' '}
              <span>{LocalStrings.dynamic.kLoginText}</span>
            </div>

            <span
              className="custom-text4-medium-primary font-icomoon cursor-pointer"
              onClick={closeModal}
            >
              {appIcons.crossIcon}
            </span>
          </header>

          {_.isEqual(layout, ALLOWED_LAYOUTS.email) ? (
            <>
              <div
                className={`custom-text1-normal-secondary ${
                  isEnabledSSO && !showSSODesc ? 'mb-3' : 'mb-5'
                }`}
              >
                {isEnabledSSO && showSSODesc
                  ? LocalStrings.dynamic.kSSOLoginDesc
                  : LocalStrings.dynamic.kEnterRegistrationEmail}
              </div>

              {/* --- Email --- */}

              {showSSODesc ? (
                <>
                  <Button
                    className="custom-button-primary-lg w-100 position-relative mb-3"
                    type="button"
                    onClick={() =>
                      handleExternalLinkRedirects(ALLOWED_REDIRECTS.sso)
                    }
                  >
                    <div className="">{LocalStrings.dynamic.kLoginWithSSO}</div>

                    <div
                      className="position-absolute d-flex align-items-center h-100 pr-2"
                      style={{ top: 0, right: 0 }}
                    >
                      {loading ? (
                        <i className={`fa fa-refresh fa-spin`}></i>
                      ) : (
                        <span
                          className="font-icomoon"
                          style={{ fontSize: '1.125rem', rotate: '-180deg' }}
                        >
                          {appIcons.arrowLeftIcon}
                        </span>
                      )}
                    </div>
                  </Button>

                  <span
                    className="custom-text9-normal-primary-hover cursor-pointer d-flex justify-content-center align-items-center"
                    onClick={() => setShowSSODesc(false)}
                  >
                    {LocalStrings.dynamic.kLoginWithEmail}
                  </span>
                </>
              ) : (
                <>
                  <div
                    className="d-flex flex-column mb-3"
                    style={{ gap: '0.5rem' }}
                  >
                    {usingPhone ? (
                      <PhoneInput
                        label={LocalStrings.dynamic.kPhoneNumber}
                        input={{
                          value: loginCreds?.email,
                          onChange: val => {
                            setLoginCreds({
                              ...loginCreds,
                              email: val
                            });
                          }
                        }}
                        meta={{
                          touched: true,
                          error: validations?.email
                        }}
                      />
                    ) : (
                      <div
                        className={`form-group row mx-0 mb-0${
                          !_.isEmpty(validations?.email) ? ' has-danger' : ''
                        }`}
                      >
                        <label className={`control-label-top col-sm-12 p-0`}>
                          {LocalStrings.dynamic.kEmail}
                        </label>
                        <div className={`col-sm-12 col-sm-offset-0 px-0`}>
                          <input
                            className="form-control input-field"
                            type={'text'}
                            placeholder={
                              LocalStrings.dynamic.kEnterEmailAddress
                            } // using same for both cases
                            name="email"
                            value={loginCreds?.email}
                            onChange={e => {
                              setLoginCreds({
                                ...loginCreds,
                                email: e.target.value
                              });
                            }}
                          />
                          <div className="text-help">
                            {validations?.email ?? ''}
                          </div>
                        </div>
                      </div>
                    )}

                    {allowPhoneLogin && (
                      <span
                        className="custom-text8-medium-theme-underline"
                        onClick={() => setUsingPhone(!usingPhone)}
                      >
                        {usingPhone
                          ? LocalStrings.dynamic.kUseEmailText
                          : LocalStrings.dynamic.kUsePhoneText}
                      </span>
                    )}
                  </div>

                  {/* --- Terms --- */}
                  <div className="d-flex flex-column mb-3">
                    <div className="d-flex mb-1">
                      <div>
                        <label
                          className={`custom-container-checkbox${
                            loginCreds.tnc ? ' checked ' : ' '
                          }custom-text1-normal mb-0`}
                        >
                          {LocalStrings.dynamic.kPleaseAcceptThe}

                          <input
                            name={'terms'}
                            type={'checkbox'}
                            checked={loginCreds.tnc}
                            onChange={() =>
                              setLoginCreds({
                                ...loginCreds,
                                tnc: !loginCreds.tnc
                              })
                            }
                          />

                          <span className="checkmark" />
                        </label>
                      </div>

                      <span
                        className="ml-1 custom-text1-medium-secondary-link cursor-pointer"
                        onClick={() =>
                          handleExternalLinkRedirects(ALLOWED_REDIRECTS.terms)
                        }
                      >
                        {LocalStrings.dynamic.kTermsAndConditions}
                      </span>
                    </div>

                    <div
                      className={`text-help label-text-help${
                        _.isEmpty(validations.tnc) ? ' mb-2' : ''
                      }`}
                    >
                      <span>{validations?.tnc ?? ''}</span>
                    </div>
                  </div>
                </>
              )}
            </>
          ) : _.isEqual(layout, ALLOWED_LAYOUTS.pw) ? (
            <>
              <div
                className="custom-text8-medium-primary mb-5 cursor-pointer d-flex align-items-center"
                onClick={handleBackToEmailLayout}
              >
                <span className="font-icomoon" style={{ fontSize: '1.125rem' }}>
                  {appIcons.arrowLeftIcon}
                </span>

                <span className="ml-1">{LocalStrings.dynamic.kGoBack}</span>
              </div>

              <div className="mb-5">
                <div
                  className={`form-group row mx-0 mb-0${
                    !_.isEmpty(validations?.passcode) ? ' has-danger' : ''
                  }`}
                >
                  <label className={`control-label-top col-sm-12 p-0`}>
                    {LocalStrings.dynamic.kEnterPasscodeText}
                  </label>
                  <div className={`col-sm-12 col-sm-offset-0 px-0`}>
                    <input
                      name="passcode"
                      type={'password'}
                      className="form-control input-field"
                      placeholder={LocalStrings.dynamic.kEnterPasscode}
                      value={loginCreds?.passcode ?? ''}
                      onChange={e => {
                        setLoginCreds({
                          ...loginCreds,
                          passcode: e.target.value
                        });
                      }}
                    />
                    <div className="text-help">
                      {validations?.passcode ?? ''}
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            ''
          )}

          {/* ----- Credential submit buttons ----- */}
          {!showSSODesc && (
            <Button
              className="custom-button-primary-lg w-100 position-relative"
              type="button"
              disabled={loading}
              onClick={handleCredentials}
            >
              <div className="">{LocalStrings.dynamic.kContinueText}</div>

              <div
                className="position-absolute d-flex align-items-center h-100 pr-2"
                style={{ top: 0, right: 0 }}
              >
                {loading ? (
                  <i className={`fa fa-refresh fa-spin`}></i>
                ) : (
                  <span
                    className="font-icomoon"
                    style={{ fontSize: '1.125rem', rotate: '-180deg' }}
                  >
                    {appIcons.arrowLeftIcon}
                  </span>
                )}
              </div>
            </Button>
          )}

          {/* ----- Resent OTP button ----- */}
          <div className="d-flex justify-content-center align-items-center mt-2">
            {_.isEqual(layout, ALLOWED_LAYOUTS.pw) && (
              <div
                className={`cursor-${!isDisabled ? 'pointer' : 'not-allowed'}`}
                style={{ opacity: isDisabled ? '0.7' : 1 }}
              >
                <span
                  className="custom-text9-normal-primary"
                  onClick={() => (isDisabled ? {} : handleResendOTP())}
                >
                  {LocalStrings.dynamic.kResendOTPText}
                </span>

                {secondsLeft > 0 && (
                  <span className="ml-1 custom-text9-normal-theme">
                    00:{String(secondsLeft).padStart(2, '0')}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* ----- Contact support section ----- */}
          <div
            className={`d-flex justify-content-between align-items-center w-100 mt-3`}
          >
            <div className="">
              <div className="custom-text8-normal-secondary">
                <span>{LocalStrings.dynamic.kProblemLoginIn}</span>
              </div>
            </div>

            <div className="d-flex align-items-center justify-content-end support-details custom-text8-medium">
              <i className="fa fa-envelope mr-2" aria-hidden="true"></i>

              <span
                className="custom-text1-medium-secondary-link"
                onClick={() =>
                  handleExternalLinkRedirects(ALLOWED_REDIRECTS.support)
                }
              >
                {LocalStrings.dynamic.kContactSupport}
              </span>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

const mapStateToProps = state => {
  // --- Edit profile redirect based on event settings
  const eventCustomSettings = state.eventSettings?.custom_settings;
  const openEditProfile = !_.isNull(eventCustomSettings)
    ? _.isEqual(
        eventCustomSettings?.open_profile_after_login,
        MANDATORY_PROFILE_OPEN_TYPES.always
      )
    : false;

  const isEnabledSSO = eventCustomSettings?.sso?.enabled;

  return {
    isLoggedIn: state.isLoggedIn,
    session: state.session,
    modalStatus: state.loginModalReducer,
    eventDetails: state.eventDetails,
    eventSettings: state.eventSettings,
    userDetails: state.userDetails,
    openEditProfile,
    isEnabledSSO
  };
};

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      loginModal,
      loginWithEmail,
      loginWithPassCode,
      setProfilePortalToggle
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(SignIn);
